import React, { useState, useEffect } from 'react';
import { auth, createUserWithEmailAndPassword } from './auth-compat';
import { db, getCollectionName } from './firebase';
import { collection, doc, setDoc, getDocs, query, where } from './firestore-compat';
import ContadorEmails from './components/ContadorEmails';

const STEPS = ['Identidade', 'Perfil', 'Setor & Equipe', 'Revisão'];

// ── Estilos ──────────────────────────────────────────────────────────────────
const T = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal:   { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' },
  header:  { padding: '22px 28px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: '#fff', zIndex: 2 },
  body:    { padding: '24px 28px', flex: 1 },
  footer:  { padding: '14px 28px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, background: '#fff' },
  label:   { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  inp:     { padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.92rem', width: '100%', boxSizing: 'border-box', outline: 'none', transition: 'border 0.15s', color: '#1e293b' },
  btnPri:  { background: '#6366f1', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem', transition: 'opacity 0.15s' },
  btnSec:  { background: 'none', color: '#64748b', border: '1px solid #e2e8f0', padding: '10px 22px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' },
  errBox:  { background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c', padding: '10px 14px', borderRadius: 8, fontSize: '0.88rem', marginBottom: 16 },
};

// ── Sub-componentes ──────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  return (
    <div style={{ padding: '18px 28px 0', display: 'flex', gap: 0 }}>
      {STEPS.map((label, i) => {
        const idx = i + 1;
        const done    = step > idx;
        const current = step === idx;
        return (
          <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {i > 0 && (
              <div style={{ position: 'absolute', left: '-50%', right: '50%', top: 14, height: 2, background: done || current ? '#6366f1' : '#e2e8f0', zIndex: 0 }} />
            )}
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? '#6366f1' : current ? '#6366f1' : '#e2e8f0', color: done || current ? '#fff' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.82rem', zIndex: 1, transition: 'all 0.2s' }}>
              {done ? '✓' : idx}
            </div>
            <div style={{ fontSize: '0.7rem', color: current ? '#6366f1' : done ? '#64748b' : '#94a3b8', fontWeight: current ? 700 : 400, marginTop: 4, whiteSpace: 'nowrap' }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

function FieldGroup({ children, cols = 1 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14, marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : {}}>
      <label style={T.label}>{label}</label>
      {children}
    </div>
  );
}

// Abas administrativas do INEP — precisa bater com ADMIN_TABS em InepEnsino.js
const INEP_ADMIN_TABS_TODAS = ['painel', 'alunos', 'cursos_admin', 'turmas', 'acompanhamento', 'avaliacoes_notas', 'certificados_admin', 'mensagens_admin', 'relatorios', 'personalizacao', 'acesso'];
function slugEmailInep(email) { return (email || '').toLowerCase().replace(/[^a-z0-9]/gi, '_'); }

// ── Componente principal ─────────────────────────────────────────────────────
export default function UserCreationWizard({ setores = [], equipes = [], cooperativas = [], onCreated, onClose }) {
  const [step, setStep] = useState(1);

  // Perfis carregados do Firestore (leitura única, sem listener em tempo real)
  const [perfisFS, setPerfisFS] = useState([]);
  const [loadingPerfis, setLoadingPerfis] = useState(true);
  useEffect(() => {
    getDocs(collection(db, getCollectionName('perfis_config')))
      .then(snap => {
        setPerfisFS(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      })
      .catch(err => console.warn('Wizard: erro ao carregar perfis:', err.message))
      .finally(() => setLoadingPerfis(false));
  }, []);

  // Step 1 — Identidade
  const [nome,        setNome]        = useState('');
  const [email,       setEmail]       = useState('');
  const [agendaName,  setAgendaName]  = useState('');

  // Step 2 — Perfil
  const [profileId, setProfileId] = useState('');

  // Step 3 — Setor & Equipe
  const [setor,  setSetor]  = useState('');
  const [equipe, setEquipe] = useState('');
  const [inicioTrabalho, setInicioTrabalho] = useState(''); // INEP acompanha os cursos obrigatórios até essa data
  const [inepAcesso, setInepAcesso] = useState('nenhum'); // 'nenhum' | 'aluno' | 'admin'

  // Criação
  const [creating, setCreating] = useState(false);
  const [error,    setError]    = useState('');
  const [created,  setCreated]  = useState(false);

  const profile = perfisFS.find(p => p.id === profileId);

  // Validações por step
  const canNext = {
    1: nome.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    2: !!profileId,
    3: (!profile?.requiresSetor || !!setor) && (!profile?.requiresEquipe || !!equipe),
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const cleanEmail = email.trim().toLowerCase();
      const snap = await getDocs(query(collection(db, getCollectionName('users')), where('email', '==', cleanEmail)));
      if (!snap.empty) throw new Error('Este e-mail já está cadastrado no sistema.');

      // Sem senha: a conta nasce com senha aleatória (gerada no servidor) e a pessoa
      // cria a própria em "Primeiro acesso" na tela de login, com código por e-mail.
      const { user } = await createUserWithEmailAndPassword(auth, cleanEmail);

      const permissoesFinais = { ...(profile?.permissions || {}) };
      if (inepAcesso !== 'nenhum') permissoesFinais.inep_ensino = { view: true, edit: false };

      await setDoc(doc(db, getCollectionName('users'), user.uid), {
        name:                nome.trim(),
        agendaName:          agendaName.trim() || nome.trim().split(' ')[0],
        email:               cleanEmail,
        equipe:              equipe.trim(),
        setor:               setor.trim(),
        inicioTrabalho:      inicioTrabalho || '',
        permissions:         permissoesFinais,
        roles:               profile?.roles || [],
        perfilId:            profile?.id || null,
        perfilName:          profile?.name || null,
        forcePasswordChange: false,
        createdAt:           new Date(),
        uid:                 user.uid,
      });

      if (inepAcesso === 'admin') {
        await setDoc(doc(db, getCollectionName('inep_admins'), slugEmailInep(cleanEmail)), {
          nome: nome.trim(), email: cleanEmail, abas: INEP_ADMIN_TABS_TODAS,
        });
      }

      setCreated(true);
      onCreated?.();
    } catch (err) {
      setError(
        err.code === 'auth/email-already-in-use' ? 'Este e-mail já está em uso no Firebase.' :
        'Erro ao criar usuário: ' + err.message
      );
    } finally {
      setCreating(false);
    }
  };

  // ── Tela de sucesso ──────────────────────────────────────────────────────
  if (created) {
    return (
      <div style={T.overlay}>
        <div style={{ ...T.modal, maxWidth: 440 }}>
          <div style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: '4rem', marginBottom: 12 }}>🎉</div>
            <h3 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '1.25rem' }}>Usuário criado com sucesso!</h3>
            <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 24px' }}>Envie as orientações de primeiro acesso para a pessoa</p>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 20px', marginBottom: 16, textAlign: 'left' }}>
              <div style={{ marginBottom: 8 }}>
                <span style={T.label}>E-mail</span>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>{email}</div>
              </div>
              {profile && (
                <div>
                  <span style={T.label}>Perfil</span>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: `${profile.color}15`, border: `1px solid ${profile.color}40`, color: profile.color, fontWeight: 600, fontSize: '0.85rem' }}>
                    {profile.icon} {profile.name || profile.label}
                  </div>
                </div>
              )}
            </div>

            <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '12px 16px', margin: '0 0 12px', textAlign: 'left', fontSize: '0.85rem', color: '#3730a3', lineHeight: 1.5 }}>
              <strong>Como a pessoa entra:</strong> acessar <strong>gestaoquality.com.br</strong> → clicar em <strong>"Primeiro acesso"</strong> → digitar este e-mail → receber um código de 6 números → criar a própria senha.
            </div>
            <div style={{ margin: '0 0 24px', textAlign: 'left' }}><ContadorEmails /></div>

            <button onClick={onClose} style={{ ...T.btnPri, padding: '11px 32px', fontSize: '0.95rem' }}>Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Wizard ───────────────────────────────────────────────────────────────
  return (
    <div style={T.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={T.modal}>

        {/* Header */}
        <div style={T.header}>
          <div>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem', fontWeight: 700 }}>➕ Novo Usuário</h3>
            <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Passo {step} de {STEPS.length} — {STEPS[step - 1]}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>✕</button>
        </div>

        <ProgressBar step={step} />

        {/* Body */}
        <div style={T.body}>
          {error && <div style={T.errBox}>{error}</div>}

          {/* ── STEP 1: IDENTIDADE ── */}
          {step === 1 && (
            <div>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.88rem' }}>Preencha o nome completo e o sistema vai gerar o e-mail automaticamente.</p>
              <FieldGroup cols={2}>
                <Field label="Nome completo *" span={2}>
                  <input style={T.inp} type="text" placeholder="Ex: Oliver Barea" value={nome} onChange={e => setNome(e.target.value)} autoFocus />
                </Field>

                <Field label="E-mail de acesso *" span={2}>
                  <input
                    style={T.inp} type="email" value={email} placeholder="E-mail que a pessoa usa no dia a dia"
                    onChange={e => setEmail(e.target.value.replace(/^\s*mailto:/i, ''))}
                  />
                </Field>

                <Field label="Nome na agenda (apelido)" span={2}>
                  <input style={T.inp} type="text" placeholder="Ex: Dr. Oliver" value={agendaName} onChange={e => setAgendaName(e.target.value)} />
                </Field>
              </FieldGroup>

              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: '0.84rem', color: '#92400e', lineHeight: 1.5 }}>
                📧 Use o <strong>e-mail real</strong> da pessoa — é nele que ela recebe o código para criar a própria senha no primeiro acesso.
              </div>
            </div>
          )}

          {/* ── STEP 2: PERFIL ── */}
          {step === 2 && (
            <div>
              <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.88rem' }}>Escolha o perfil que define o que este usuário pode acessar.</p>
              {loadingPerfis && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Carregando perfis...</div>}
              {!loadingPerfis && perfisFS.length === 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '16px 20px', color: '#92400e', fontSize: '0.88rem' }}>
                  ⚠️ Nenhum perfil cadastrado ainda. Acesse <strong>Configurações → Perfis</strong> para criar os perfis antes de cadastrar usuários.
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {perfisFS.map(p => {
                  const selected = profileId === p.id;
                  return (
                    <button key={p.id} onClick={() => setProfileId(p.id)} style={{
                      background: selected ? `${p.color}12` : '#f8fafc',
                      border: `2px solid ${selected ? p.color : '#e2e8f0'}`,
                      borderRadius: 12, padding: '14px 12px', cursor: 'pointer',
                      textAlign: 'left', transition: 'all 0.15s', position: 'relative',
                    }}>
                      {selected && <span style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.8rem' }}>✅</span>}
                      <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>{p.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: selected ? p.color : '#1e293b', marginBottom: 3 }}>{p.name || p.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>{p.desc}</div>
                      {(p.requiresSetor || p.requiresEquipe) && (
                        <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {p.requiresSetor  && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.66rem', fontWeight: 700, padding: '2px 6px', borderRadius: 20 }}>Setor</span>}
                          {p.requiresEquipe && <span style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.66rem', fontWeight: 700, padding: '2px 6px', borderRadius: 20 }}>Equipe</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP 3: SETOR & EQUIPE ── */}
          {step === 3 && (
            <div>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.88rem' }}>
                {profile?.requiresSetor
                  ? `O perfil "${profile.name || profile.label}" requer que o setor seja definido.`
                  : 'Setor e equipe são opcionais para este perfil.'}
              </p>
              <FieldGroup cols={1}>
                <Field label={`Setor${profile?.requiresSetor ? ' *' : ' (opcional)'}`}>
                  <select style={{ ...T.inp, background: '#fff' }} value={setor} onChange={e => setSetor(e.target.value)}>
                    <option value="">— Selecione o setor —</option>
                    {setores.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  {profile?.requiresSetor && !setor && (
                    <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#f59e0b' }}>⚠️ Este perfil exige um setor configurado para acesso às ocorrências.</p>
                  )}
                </Field>

                <Field label={`Equipe / Cooperativa${profile?.requiresEquipe ? ' *' : ' (opcional)'}`}>
                  <select style={{ ...T.inp, background: '#fff' }} value={equipe} onChange={e => setEquipe(e.target.value)}>
                    <option value="">— Sem equipe / cooperativa —</option>
                    {cooperativas.length > 0 && (
                      <optgroup label="Cooperativas Parceiras">
                        {cooperativas.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </optgroup>
                    )}
                    {equipes.length > 0 && (
                      <optgroup label="Equipes Internas">
                        {equipes.map(eq => <option key={eq.id} value={eq.name}>{eq.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </Field>
              </FieldGroup>

              <Field label="Início no trabalho (opcional)">
                <input type="date" style={{ ...T.inp, background: '#fff' }} value={inicioTrabalho} onChange={e => setInicioTrabalho(e.target.value)} />
                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b' }}>Com a data e a pessoa numa turma do INEP, o RH e o gerente da área são avisados se os cursos não estiverem feitos até esse dia.</p>
              </Field>

              <Field label="Acesso à plataforma INEP (ensino)">
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['nenhum', 'Nenhum'], ['aluno', '🎓 Aluno'], ['admin', '🛠️ Admin']].map(([val, label]) => (
                    <button key={val} onClick={() => setInepAcesso(val)} style={{
                      padding: '9px 16px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                      border: `1.5px solid ${inepAcesso === val ? '#0878b9' : '#e2e8f0'}`,
                      background: inepAcesso === val ? '#e7f3fb' : '#fff',
                      color: inepAcesso === val ? '#065f95' : '#64748b',
                    }}>{label}</button>
                  ))}
                </div>
                <p style={{ margin: '6px 0 0', fontSize: '0.76rem', color: '#94a3b8' }}>
                  {inepAcesso === 'admin' ? 'Terá acesso a todas as áreas administrativas do INEP.' : inepAcesso === 'aluno' ? 'Poderá acessar cursos, avaliações e certificados no INEP.' : 'A aba INEP não vai aparecer pra este usuário.'}
                </p>
              </Field>
            </div>
          )}

          {/* ── STEP 4: REVISÃO ── */}
          {step === 4 && (
            <div>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.88rem' }}>Confirme as informações antes de criar o usuário.</p>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                {/* Identidade */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={T.label}>Identidade</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>{nome}</div>
                  <div style={{ fontSize: '0.9rem', color: '#6366f1', fontWeight: 600 }}>{email}</div>
                  {agendaName && <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>Agenda: {agendaName}</div>}
                </div>

                {/* Perfil */}
                {profile && (
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: `${profile.color}15`, border: `2px solid ${profile.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>{profile.icon}</div>
                    <div>
                      <div style={T.label}>Perfil</div>
                      <div style={{ fontWeight: 700, color: profile.color }}>{profile.name || profile.label}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{profile.desc}</div>
                    </div>
                  </div>
                )}

                {/* Setor / Equipe */}
                <div style={{ padding: '16px 20px', display: 'flex', gap: 32 }}>
                  <div>
                    <div style={T.label}>Setor</div>
                    <div style={{ color: setor ? '#1e293b' : '#94a3b8', fontSize: '0.9rem' }}>{setor || '— não definido —'}</div>
                  </div>
                  <div>
                    <div style={T.label}>Equipe / Cooperativa</div>
                    <div style={{ color: equipe ? '#1e293b' : '#94a3b8', fontSize: '0.9rem' }}>{equipe || '— não definida —'}</div>
                  </div>
                  <div>
                    <div style={T.label}>Início no trabalho</div>
                    <div style={{ color: inicioTrabalho ? '#1e293b' : '#94a3b8', fontSize: '0.9rem' }}>{inicioTrabalho ? inicioTrabalho.split('-').reverse().join('/') : '— não informado —'}</div>
                  </div>
                  <div>
                    <div style={T.label}>Senha</div>
                    <div style={{ color: '#4f46e5', fontSize: '0.9rem', fontWeight: 600 }}>Criada pela pessoa no 1º acesso</div>
                  </div>
                  <div>
                    <div style={T.label}>Acesso ao INEP</div>
                    <div style={{ color: inepAcesso === 'nenhum' ? '#94a3b8' : '#1e293b', fontSize: '0.9rem', fontWeight: 600 }}>
                      {inepAcesso === 'admin' ? '🛠️ Admin' : inepAcesso === 'aluno' ? '🎓 Aluno' : '— nenhum —'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={T.footer}>
          {step > 1
            ? <button onClick={() => { setError(''); setStep(s => s - 1); }} style={T.btnSec}>← Voltar</button>
            : <button onClick={onClose} style={T.btnSec}>Cancelar</button>
          }

          {step < 4
            ? (
              <button
                onClick={() => { setError(''); setStep(s => s + 1); }}
                disabled={!canNext[step]}
                style={{ ...T.btnPri, opacity: canNext[step] ? 1 : 0.4, cursor: canNext[step] ? 'pointer' : 'not-allowed' }}
              >
                Próximo →
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={creating}
                style={{ ...T.btnPri, background: '#10b981', opacity: creating ? 0.7 : 1, cursor: creating ? 'not-allowed' : 'pointer', minWidth: 140 }}
              >
                {creating ? '⏳ Criando...' : '✅ Criar Usuário'}
              </button>
            )
          }
        </div>
      </div>
    </div>
  );
}
